import React from 'react';
import { NotFound } from '@vyaguta/vyaguta-ui';
import { Redirect, Route, Switch, type RouteComponentProps } from 'react-router-dom';

import Employees from './employees';
import Dashboard from './dashboard';
import history from 'utils/history';
import Settings from './settings/Settings';
import { interpolate } from 'utils/string';
import UpdateEmployee from './employees/updateEmployee';
import ChangeTeamManager from './employees/profile/teamManagers';
import NoteSummary from './notes/NoteSummary';
import AuthorizedRoute from './AuthorizedRoute';
import NoticeBoard from './noticeBoard';
import RolesPermissions from './rolesPermissions';
import CreateEmployeeForm from './employees/createEmployee';
import ChangeDesignation from './employees/profile/changeDesignation';
import * as permissions from 'constants/permissions';
import EmployeeSkills from './employees/profile/skills';
import EmployeeProfile from './employees/profile';
import ChangeEngagement from './employees/profile/changeEngagement';
import ChangeLeaveIssuer from './employees/profile/changeLeaveIssuer';
import EventsAndNotices from './eventsAndNotices/EventsAndNotices';
import { ResourceFinder, ResourceFinderV1 } from './resourceFinder';
import ChangeIssuerRequest from './employees/profile/changeIssuerRequest';
import ChangeLeaveIssuerList from './issuers/changeRequests/ChangeLeaveIssuerList';
import EmployeeAssets from './employees/profile/assets/EmployeeAssets';
import ChangeTentativeExit from './employees/profile/changeTentativeExit';

import {
  CREATE_EMPLOYEE,
  RESOURCE_FINDER,
  UPDATE_EMPLOYEE,
  EMPLOYEE_SKILLS,
  EMPLOYEE_PROFILE,
  ACTIVE_EMPLOYEES,
  CHANGE_DESIGNATION,
  UPDATE_LEAVE_ISSUER,
  UPDATE_TEAM_MANAGER,
  DASHBOARD,
  EMPLOYEE_PROFILE_LEAVE_CREDITS,
  EMPLOYEE_PROFILE_GOALS,
  EMPLOYEE_PROFILE_FEEDBACKS,
  ROLES_PERMISSIONS,
  ME,
  HOME,
  CHANGE_ENGAGEMENT,
  NOTE_SUMMARY,
  NOTICE_BOARD,
  CHANGE_ISSUER_REQUEST,
  LEAVE_ISSUER_CHANGE_REQUESTS,
  EVENTS_AND_NOTICES,
  SETTINGS,
  CHANGE_TENTATIVE_EXIT,
  RESOURCE_FINDER_V1,
  EMPLOYEE_ASSETS,
} from 'constants/routes';
import { ROLE } from 'constants/appConstants';

import type { DecodedTokenData } from 'interface/token';

export interface PageRouterProps {
  user: DecodedTokenData;
  noticeCount?: number;
  isNoticeCountLoading?: boolean;
}

type EmployeeIdParams = {
  id: string;
};

const PageRouter: React.FC<PageRouterProps> = (props) => {
  const currentUserId = props.user.id ?? '';

  return (
    <Switch>
      <AuthorizedRoute
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.EMPLOYEES.CREATE]}
        path={CREATE_EMPLOYEE}
        component={CreateEmployeeForm}
      />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        path={RESOURCE_FINDER_V1}
        permission={[permissions.EMPLOYEES.FINDER]}
        component={ResourceFinderV1}
        {...props}
      />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        path={RESOURCE_FINDER}
        permission={[permissions.EMPLOYEES.FINDER]}
        component={ResourceFinder}
        {...props}
      />
      <AuthorizedRoute
        exact
        permission={[permissions.EMPLOYEES.SKILLS.UPDATE]}
        path={EMPLOYEE_SKILLS}
        component={EmployeeSkills}
        allowSelf={true}
        {...props}
      />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.EMPLOYEES.UPDATE_LEAVE_ISSUER]}
        path={UPDATE_LEAVE_ISSUER}
        component={ChangeLeaveIssuer}
        {...props}
      />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.EMPLOYEES.UPDATE_TEAM_MANAGER]}
        path={UPDATE_TEAM_MANAGER}
        component={ChangeTeamManager}
        {...props}
      />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.EMPLOYEES.ENGAGEMENTS.UPDATE]}
        path={CHANGE_ENGAGEMENT}
        component={ChangeEngagement}
        {...props}
      />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.EMPLOYEES.DESIGNATIONS.UPDATE]}
        path={CHANGE_DESIGNATION}
        component={ChangeDesignation}
        {...props}
      />
      <AuthorizedRoute
        permission={[permissions.EMPLOYEES.PROFILE.UPDATE]}
        path={UPDATE_EMPLOYEE}
        allowSelf={true}
        component={UpdateEmployee}
        {...props}
      />
      <AuthorizedRoute
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.EMPLOYEES.RESIGNATIONS.VIEW]}
        path={CHANGE_TENTATIVE_EXIT}
        allowSelf={false}
        component={ChangeTentativeExit}
        {...props}
      />
      <Route
        exact
        path={CHANGE_ISSUER_REQUEST}
        render={(empProps) => <ChangeIssuerRequest {...empProps} {...props} />}
      />
      <Route exact path={ACTIVE_EMPLOYEES} render={(empProps) => <Employees {...empProps} {...props} />} />
      <Route exact path={DASHBOARD} render={(empProps) => <Dashboard {...empProps} {...props} />} />
      <Route path={EMPLOYEE_PROFILE} render={(empProps) => <EmployeeProfile {...empProps} {...props} />} />
      <AuthorizedRoute
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.NOTES.VIEW]}
        path={NOTE_SUMMARY}
        component={NoteSummary}
        {...props}
      />
      <AuthorizedRoute
        permission={[permissions.EMPLOYEES.ASSETS.VIEW]}
        allowSelf={true}
        path={EMPLOYEE_ASSETS}
        render={(empProps) => <EmployeeAssets employeeId={empProps.match.params.id} />}
      />
      <Route exact path={NOTICE_BOARD} render={(routeProps) => <NoticeBoard {...routeProps} />} />
      <AuthorizedRoute
        exact
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.LEAVE_ISSUER_CHANGE_REQUESTS.VIEW]}
        path={LEAVE_ISSUER_CHANGE_REQUESTS}
        component={ChangeLeaveIssuerList}
        {...props}
      />

      <AuthorizedRoute
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[
          permissions.NOTICES.VIEW,
          permissions.EVENTS.VIEW,
          permissions.REMINDERS.VIEW,
          permissions.HOLIDAYS.VIEW,
        ]}
        path={EVENTS_AND_NOTICES}
        component={EventsAndNotices}
        {...props}
      />

      <AuthorizedRoute
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        permission={[permissions.SKILLS.VIEW, permissions.DESIGNATIONS.VIEW, permissions.USER_PERMISSIONS.VIEW]}
        path={SETTINGS}
        component={Settings}
        {...props}
      />

      <Route
        exact
        path="/employees/:id"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(EMPLOYEE_PROFILE, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/goals"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(EMPLOYEE_PROFILE_GOALS, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/feedbacks"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(EMPLOYEE_PROFILE_FEEDBACKS, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/leave-credits"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(EMPLOYEE_PROFILE_LEAVE_CREDITS, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/assets"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(EMPLOYEE_ASSETS, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/update"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(UPDATE_EMPLOYEE, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/skills"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(EMPLOYEE_SKILLS, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/designations"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(CHANGE_DESIGNATION, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/leave-issuers"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(UPDATE_LEAVE_ISSUER, { id: routeProps.match.params.id })} />
        )}
      />
      <Route
        exact
        path="/employees/:id/team-managers"
        render={(routeProps: RouteComponentProps<EmployeeIdParams>) => (
          <Redirect to={interpolate(UPDATE_TEAM_MANAGER, { id: routeProps.match.params.id })} />
        )}
      />
      <AuthorizedRoute
        forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
        exact
        path={ROLES_PERMISSIONS}
        component={RolesPermissions}
        {...props}
      />
      <Redirect from={ME} to={interpolate(EMPLOYEE_PROFILE, { id: currentUserId })} />
      <Redirect from="/employees/" to={ACTIVE_EMPLOYEES} />
      <Route render={() => <NotFound redirectHandler={() => history.push(HOME)} className="flash-card" />} />
    </Switch>
  );
};

export default PageRouter;
